/**
 * SectionEditor — renders a form for one section's editable props.
 *
 * Props:
 *   schema   { label, fields }  — from sectionFieldSchemas
 *   props    object             — current prop values
 *   onChange (newProps) => void — called with full updated props object
 */

import React, { useCallback, useState, useEffect } from 'react';
import {
  Box, TextField, Switch, FormControlLabel, Typography,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  Button, Divider, Tooltip, useTheme, Autocomplete, Chip as MuiChip,
  CircularProgress,
} from '@mui/material';
import {
  Add, Delete, ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import { ICON_ALLOWLIST } from './sectionFieldSchemas';
import { resolveIcon } from '../../../marketing/editable/iconRegistry';
import { glass } from '../ui/glassTokens';
import api from '../../../utils/api';

// ─── small uid helper ─────────────────────────────────────────────────────────
function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─── StringListEditor — add/remove/reorder list of strings ───────────────────
function StringListEditor({ value, onChange }) {
  // Tolerate non-array / undefined values (e.g. when a tier is first added)
  const safeValue = Array.isArray(value) ? value : [];
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleChange = useCallback((idx, newVal) => {
    const next = safeValue.map((v, i) => (i === idx ? newVal : v));
    onChange(next);
  }, [safeValue, onChange]);

  const handleAdd = useCallback(() => {
    onChange([...safeValue, '']);
  }, [safeValue, onChange]);

  const handleRemove = useCallback((idx) => {
    onChange(safeValue.filter((_, i) => i !== idx));
  }, [safeValue, onChange]);

  const handleMove = useCallback((idx, dir) => {
    const next = [...safeValue];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }, [safeValue, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {safeValue.map((item, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={item}
            onChange={e => handleChange(idx, e.target.value)}
            sx={{ flex: 1 }}
          />
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" disabled={idx === 0} onClick={() => handleMove(idx, -1)}>
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" disabled={idx === safeValue.length - 1} onClick={() => handleMove(idx, 1)}>
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={() => handleRemove(idx)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<Add />}
        onClick={handleAdd}
        variant="outlined"
        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
      >
        Add item
      </Button>
    </Box>
  );
}

// ─── ItemListEditor — repeatable sub-forms with add/remove/reorder ────────────
function ItemListEditor({ value = [], itemFields, onChange }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const handleItemChange = useCallback((idx, key, newVal) => {
    const next = value.map((item, i) =>
      i === idx ? { ...item, [key]: newVal } : item
    );
    onChange(next);
  }, [value, onChange]);

  const handleAdd = useCallback(() => {
    const newItem = { id: uid() };
    itemFields.forEach(f => {
      if (f.kind === 'bool') newItem[f.key] = false;
      else if (f.kind === 'list') newItem[f.key] = [];
      else newItem[f.key] = '';
    });
    onChange([...value, newItem]);
  }, [value, itemFields, onChange]);

  const handleRemove = useCallback((idx) => {
    onChange(value.filter((_, i) => i !== idx));
  }, [value, onChange]);

  const handleMove = useCallback((idx, dir) => {
    const next = [...value];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }, [value, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {value.map((item, idx) => (
        <Box
          key={item.id || idx}
          sx={{
            ...g.surface,
            borderRadius: 2,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Item header: index + move/remove controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Item {idx + 1}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" disabled={idx === 0} onClick={() => handleMove(idx, -1)}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" disabled={idx === value.length - 1} onClick={() => handleMove(idx, 1)}>
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove item">
                <IconButton size="small" color="error" onClick={() => handleRemove(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Sub-fields */}
          {itemFields.map(subField => (
            <SubFieldRenderer
              key={subField.key}
              field={subField}
              value={item[subField.key]}
              onChange={val => handleItemChange(idx, subField.key, val)}
            />
          ))}
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<Add />}
        onClick={handleAdd}
        variant="outlined"
        sx={{ alignSelf: 'flex-start' }}
      >
        Add item
      </Button>
    </Box>
  );
}

// ─── IconSelectField ──────────────────────────────────────────────────────────
function IconSelectField({ value, onChange }) {
  const IconComponent = value ? resolveIcon(value) : null;

  return (
    <FormControl size="small" fullWidth>
      <InputLabel>Icon</InputLabel>
      <Select
        value={value || ''}
        label="Icon"
        onChange={e => onChange(e.target.value)}
        renderValue={(selected) => {
          if (!selected) return <em>Select icon</em>;
          const Icon = resolveIcon(selected);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon sx={{ fontSize: 18 }} />
              <Typography variant="body2">{selected}</Typography>
            </Box>
          );
        }}
      >
        {ICON_ALLOWLIST.map(name => {
          const Icon = resolveIcon(name);
          return (
            <MenuItem key={name} value={name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Icon sx={{ fontSize: 20 }} />
                <Typography variant="body2">{name}</Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

// ─── LibrarySelectField — multi-select ordered list of library UUIDs ─────────
/**
 * Renders an MUI Autocomplete (multiple) populated from GET /api/libraries.
 * Value is an ordered array of library UUIDs (strings).
 * Options show library name + city. Selection order is preserved.
 * Max 12 selections as per contract.
 */
function LibrarySelectField({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Fetch library list once on mount
  useEffect(() => {
    let cancelled = false;
    api.get('/libraries', { params: { limit: 200 } })
      .then((res) => {
        if (cancelled) return;
        const libs = res.data?.data?.libraries || [];
        setOptions(libs.map(l => ({ id: l.id, name: l.name, city: l.city })));
        setLoadingOptions(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchError('Could not load libraries');
        setLoadingOptions(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Normalise value: always an array of UUID strings
  const safeValue = Array.isArray(value) ? value : [];

  // Map selected UUIDs to option objects for Autocomplete value
  const selectedOptions = safeValue
    .map(id => options.find(o => o.id === id))
    .filter(Boolean);

  const handleChange = useCallback((_event, newSelection) => {
    // Enforce max 12 selections; preserve order
    const limited = newSelection.slice(0, 12);
    onChange(limited.map(o => o.id));
  }, [onChange]);

  if (fetchError) {
    return (
      <Typography variant="caption" color="error">
        {fetchError} — save and reload to retry.
      </Typography>
    );
  }

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      loading={loadingOptions}
      getOptionLabel={(option) => `${option.name}${option.city ? ` — ${option.city}` : ''}`}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Pinned Libraries"
          placeholder={selectedOptions.length < 12 ? 'Search by name…' : 'Max 12 reached'}
          helperText={`${selectedOptions.length}/12 selected — drag to reorder is not available; use the order of selection.`}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingOptions ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <MuiChip
            key={option.id}
            label={`${index + 1}. ${option.name}${option.city ? `, ${option.city}` : ''}`}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
      disableCloseOnSelect
      filterSelectedOptions
    />
  );
}

// ─── SubFieldRenderer — renders a single field within an itemList sub-form ────
function SubFieldRenderer({ field, value, onChange }) {
  switch (field.kind) {
    case 'bool':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!value}
              onChange={e => onChange(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">{field.label}</Typography>}
        />
      );

    case 'list':
      return (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {field.label}
          </Typography>
          <StringListEditor value={value} onChange={onChange} />
        </Box>
      );

    case 'textarea':
      return (
        <TextField
          size="small"
          label={field.label}
          multiline
          minRows={2}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
        />
      );

    case 'iconSelect':
      return (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {field.label}
          </Typography>
          <IconSelectField value={value} onChange={onChange} />
        </Box>
      );

    case 'select':
      return (
        <FormControl size="small" fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value ?? ''}
            label={field.label}
            onChange={e => onChange(e.target.value)}
          >
            {(field.options || []).map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'url':
    case 'text':
    default:
      return (
        <TextField
          size="small"
          label={field.label}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          type={field.kind === 'url' ? 'url' : 'text'}
        />
      );
  }
}

// ─── FieldRenderer — top-level field in a section form ───────────────────────
function FieldRenderer({ field, value, onChange }) {
  switch (field.kind) {
    case 'librarySelect':
      return (
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {field.label}
          </Typography>
          <LibrarySelectField value={value} onChange={onChange} />
        </Box>
      );

    case 'bool':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!value}
              onChange={e => onChange(e.target.checked)}
            />
          }
          label={<Typography variant="body2" fontWeight={500}>{field.label}</Typography>}
        />
      );

    case 'textarea':
      return (
        <TextField
          label={field.label}
          multiline
          minRows={3}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          size="small"
        />
      );

    case 'list':
      return (
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {field.label}
          </Typography>
          <StringListEditor value={value} onChange={onChange} />
        </Box>
      );

    case 'itemList':
      return (
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {field.label}
          </Typography>
          <ItemListEditor value={value} itemFields={field.itemFields || []} onChange={onChange} />
        </Box>
      );

    case 'iconSelect':
      return (
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {field.label}
          </Typography>
          <IconSelectField value={value} onChange={onChange} />
        </Box>
      );

    case 'select':
      return (
        <FormControl size="small" fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value ?? ''}
            label={field.label}
            onChange={e => onChange(e.target.value)}
          >
            {(field.options || []).map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'url':
    case 'text':
    default:
      return (
        <TextField
          label={field.label}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          size="small"
          type={field.kind === 'url' ? 'url' : 'text'}
        />
      );
  }
}

// ─── SectionEditor (default export) ──────────────────────────────────────────
export default function SectionEditor({ schema, props: sectionProps, onChange }) {
  const { fields } = schema;

  const handleFieldChange = useCallback((key, newVal) => {
    onChange({ ...sectionProps, [key]: newVal });
  }, [sectionProps, onChange]);

  if (!fields || fields.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No editable fields for this section. Use the visibility toggle above to show or hide it.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 0.5 }}>
      {fields.map((field, idx) => (
        <React.Fragment key={field.key}>
          <FieldRenderer
            field={field}
            value={sectionProps?.[field.key]}
            onChange={val => handleFieldChange(field.key, val)}
          />
          {idx < fields.length - 1 && (
            <Divider sx={{ opacity: 0.4 }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
